import { DialogTitle } from "@mui/material";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import { IControl } from "commons/ultilities/dynamic-form/models/control";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { IUpdateOrder } from "mobx-stores/_models/orders/order";
import { useStores } from "mobx-stores/_root-store";
import { useResetCustomerDependentControls } from "pages/common/hooks/use-reset-customer-dependent-controls";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DialogComplexContent } from "share-components/templates/form/dialog-complex-form/dialog-complex-content";
import { DialogComplexFormContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form";
import { DialogComplexFormArrayContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form-array";

import { orderBusiness } from "api/bussiness/order/order-bussiness";
import { dateTimeToUTCString } from "commons/ultilities/date/dayjs";
import { createProductOptionLabel } from "commons/ultilities/option-item/label-ultil";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getEditOrderFormSchema } from "../schema";

export const EditOrder = observer(() => {
  const {
    orderManagement: { openEdit, getOrderDetail, setProp, orderDetail },
  } = useStores();
  const [enableDependentReset, setEnableDependentReset] = useState(false);
  const {
    form: { controls, isValid },
    getControl,
    getControlArray,
    setDefaults,
    submitHandler,
  } = useForm<IUpdateOrder>((form) => getEditOrderFormSchema(form), {
    id: "",
    poDate: dateTimeToUTCString(dayjs()),
    poNumber: "",
    customerId: "",
    items: [],
  });

  useEffect(() => {
    if (orderDetail) {
      setEnableDependentReset(false);

      setDefaults({
        id: orderDetail.id,
        poDate: orderDetail.poDate,
        poNumber: orderDetail.poNumber,
        customerId: orderDetail.customerId,
        items: orderDetail.items.map((x) => ({
          unitPrice: x.unitPrice,
          compensatingQuantity: x.compensatingQuantity,
          compensatingReason: x.compensatingReason,
          deliveryDate: x.deliveryDate,
          isCompensating: x.isCompensating,
          productId: x.productId,
          quantityLower: x.quantityLower,
          quantityStandard: x.quantityStandard,
          quantityUpper: x.quantityLower,
          id: x.id,
        })),
      });
      if (orderDetail.items && orderDetail.items.length > 0) {
        getControl("items").children.forEach((row: IControl[]) => {
          const idCtr = row.filter((x) => x.name === "id")?.[0];
          const data = orderDetail.items.filter(
            (x) => idCtr && x.id === idCtr.value,
          )?.[0];
          const productIdCtr = row.filter((x) => x.name === "productId")?.[0];
          if (productIdCtr && data.product) {
            productIdCtr.setProp("optionItems", [
              {
                label: createProductOptionLabel(data.product),
                value: data.product?.id,
              },
            ]);
          }
        });
      }

      setEnableDependentReset(true);
    }
  }, [getControl, orderDetail, setDefaults]);

  useEffect(() => {
    const customerSelection = getControl("customerId");
    customerSelection.setProp("optionItems", [
      {
        label: orderDetail?.customer?.dealingName || "",
        value: orderDetail?.customer?.id,
      },
    ]);
  }, [
    getControl,
    orderDetail?.customer?.id,
    orderDetail?.customer?.dealingName,
  ]);

  const saveHandler = useCallback(
    async (data: IUpdateOrder) => {
      try {
        if (!orderDetail?.id) return;
        await orderBusiness.update(orderDetail?.id, data);
        await getOrderDetail(orderDetail?.id);
        setProp("openEdit", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getOrderDetail, orderDetail?.id, setProp],
  );

  const itemControl = useMemo(() => {
    return getControl("items");
  }, [getControl]);

  const customerControl = useMemo(() => {
    return getControl("customerId");
  }, [getControl]);

  useResetCustomerDependentControls({
    customerId: customerControl.value,
    arrayChildControls: [
      {
        arrayControl: itemControl,
        childControlName: "productId",
      },
    ],
    enabled: enableDependentReset,
  });

  const orderItems = useMemo(() => {
    return getControlArray("items");
  }, [getControlArray]);

  const removeRowHandler = useCallback(
    (idx: number) => {
      itemControl.removeChildByIndex(idx);
    },
    [itemControl],
  );

  const addNewOrderItemRow = useCallback(() => {
    itemControl.addNewChild();
  }, [itemControl]);

  const onOrderItemsValueChangedHandler = useCallback(
    (ctr: IControl, rowIdx: number) => {
      if (ctr.name === "isCompensating") {
        const item = orderItems[rowIdx];
        if (item) {
          const compensatingQuantityCtr = item.filter(
            (x) => x.name === "compensatingQuantity",
          )?.[0];
          if (compensatingQuantityCtr) {
            compensatingQuantityCtr.setProp("disabled", !ctr.value);
          }
          const compensatingReasonCtr = item.filter(
            (x) => x.name === "compensatingReason",
          )?.[0];
          if (compensatingReasonCtr) {
            compensatingReasonCtr.setProp("disabled", !ctr.value);
          }
        }
      }
    },
    [orderItems],
  );
  return (
    <DialogForm
      open={openEdit}
      fullWidth
      maxWidth={"lg"}
      onClose={() => setProp("openEdit", false)}
      onCancelBtnClicked={() => setProp("openEdit", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Sửa đơn đặt hàng</DialogTitle>
      <DialogComplexContent>
        <DialogComplexFormContainer>
          <DynamicForm controls={controls}></DynamicForm>
        </DialogComplexFormContainer>
        <DialogComplexFormArrayContainer
          label={itemControl.label}
          addNewRow={addNewOrderItemRow}
        >
          {orderItems && orderItems.length > 0 && (
            <DynamicFormArray
              onValueChanged={onOrderItemsValueChangedHandler}
              containerProps={{
                sx: {
                  minWidth: "1250px",
                  marginTop: "10px",
                  marginBottom: "10px",
                },
              }}
              children={orderItems}
              onRemoveRowClick={removeRowHandler}
            ></DynamicFormArray>
          )}
        </DialogComplexFormArrayContainer>
      </DialogComplexContent>
    </DialogForm>
  );
});
